import { useMutation, useQuery } from '@tanstack/react-query';

import type { ApiError } from '@/lib/api/error';
import { queryClient } from '@/lib/queryClient';
import { alumniKeys } from '@/routes/_staff/people/_list/alumni/-query/key';
import { peopleListKeys } from '@/routes/_staff/people/_list/workforce/-query/key';
import { personHeaderKeys } from '@/routes/_staff/person/$id/-hooks/usePersonHeader';

import { getPeopleLedgerDetail, savePeopleLedgerDetail } from './api';
import { peopleLedgerDetailKey } from './key';
import type { LargeCategoryCode, PeopleLedgerSection } from './type';

export type {
  GetPeopleLedgerDetailResponse,
  LargeCategoryCode,
  PeopleLedgerSection,
  PeopleLedgerAttribute,
  PeopleLedgerAttributeValue,
  SectionDetailResponse,
} from './type';

export function prefetchPeopleLedgerDetail(
  personId: string,
  largeCategoryCode: LargeCategoryCode,
) {
  return queryClient.ensureQueryData({
    queryKey: peopleLedgerDetailKey.detail(personId, largeCategoryCode),
    queryFn: () => getPeopleLedgerDetail(personId, largeCategoryCode),
    staleTime: 5 * 60 * 1000,
  });
}

export function useGetPeopleLedgerDetail(
  personId: string,
  largeCategoryCode: LargeCategoryCode,
) {
  return useQuery({
    queryKey: peopleLedgerDetailKey.detail(personId, largeCategoryCode),
    queryFn: () => getPeopleLedgerDetail(personId, largeCategoryCode),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSavePeopleLedgerDetail(
  personId: string,
  largeCategoryCode: LargeCategoryCode,
  options?: {
    onSuccess?: () => void;
    onError?: (error: ApiError) => void;
  },
) {
  return useMutation({
    mutationFn: async (sections: PeopleLedgerSection[]) => {
      return await savePeopleLedgerDetail(
        personId,
        largeCategoryCode,
        sections,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: peopleLedgerDetailKey.detail(personId, largeCategoryCode),
      });
      queryClient.invalidateQueries({
        queryKey: peopleListKeys.all,
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: alumniKeys.all,
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: personHeaderKeys.detail(personId),
      });
      options?.onSuccess?.();
    },
    onError: (error: ApiError) => {
      options?.onError?.(error);
    },
  });
}
